import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import adminApi from "../../services/adminApi";
import api from "../../services/api";
import toast from "react-hot-toast";
import UserServicesView from "../../components/Admin/UserServicesView";
import LoadingState from "../../components/Admin/LoadingState";
import AdminTable from "../../components/Admin/AdminTable";
import ConfirmModal from "../../components/common/ConfirmModal";
import "../../styles/Admin.css";

export default function UserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [services, setServices] = useState(null);
  const [sizes, setSizes] = useState([]);
  const [pricingSettings, setPricingSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [suspending, setSuspending] = useState(false);
  const [confirmSuspend, setConfirmSuspend] = useState(false);
  const [adminEmail, setAdminEmail] = useState(null);

  useEffect(() => {
    loadData();
    // Get current admin email
    adminApi
      .get("/admin/me")
      .then((res) => setAdminEmail(res.data.email))
      .catch(() => console.error("Failed to get admin email"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSuspendClick = () => {
    // Check if trying to suspend themselves
    if (
      adminEmail &&
      user?.email &&
      user.email.toLowerCase() === adminEmail.toLowerCase()
    ) {
      toast.error("You cannot suspend your own account");
      return;
    }
    setConfirmSuspend(true);
  };

  const handleSuspend = async () => {
    setConfirmSuspend(false);
    setSuspending(true);
    try {
      await adminApi.post(`/admin/users/${id}/suspend`);
      toast.success("User suspended successfully");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to suspend user");
    } finally {
      setSuspending(false);
    }
  };

  const handleUnsuspend = async () => {
    setSuspending(true);
    try {
      await adminApi.post(`/admin/users/${id}/unsuspend`);
      toast.success("User unsuspended successfully");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to unsuspend user");
    } finally {
      setSuspending(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [userRes, servicesRes, sizesRes, pricingRes] = await Promise.all([
        adminApi.get("/admin/users"),
        adminApi.get(`/admin/users/${id}/services`),
        api.get("/resources/sizes"),
        adminApi.get("/pricing/admin/settings").catch(() => null),
      ]);
      const foundUser = userRes.data.users.find(
        (u) => u.id === parseInt(id, 10),
      );
      if (foundUser) {
        setUser(foundUser);
        setServices(servicesRes.data);
        setSizes(sizesRes.data || []);
        setPricingSettings(pricingRes?.data || null);
      } else {
        toast.error("User not found");
        navigate("/admin/users");
      }
    } catch (error) {
      toast.error("Failed to load user data");
      navigate("/admin/users");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingState message="Loading user details..." />;
  }

  if (!user || !services) {
    return null;
  }

  // Calculate usage stats
  const stats = {
    totalDroplets: services.droplets?.length || 0,
    totalSnapshots: services.snapshots?.length || 0,
    totalBackups: services.backups?.length || 0,
    totalFirewalls: services.firewalls?.length || 0,
    totalDiskSize: (services.droplets || []).reduce(
      (sum, d) => sum + (parseInt(d.size?.match(/\d+/)?.[0]) || 0),
      0,
    ),
    activeDroplets: (services.droplets || []).filter(
      (d) => d.status === "active",
    ).length,
  };

  // Create a map of size slug to pricing (with margins applied)
  const sizePriceMap = new Map();
  sizes.forEach((size) => {
    if (size.slug && size.pricing) {
      const price =
        size.pricing.usdWithMargin ||
        size.pricing.usd ||
        size.price_monthly ||
        0;
      sizePriceMap.set(size.slug, price);
    }
  });

  // Get price for a droplet size
  const getDropletPrice = (sizeSlug) => {
    if (!sizeSlug) return 0;
    const price = sizePriceMap.get(sizeSlug);
    // If not found, try to find by matching slug pattern
    if (!price && sizeSlug) {
      // Try to find similar size (handle slight variations)
      for (const [slug, p] of sizePriceMap.entries()) {
        if (slug.includes(sizeSlug) || sizeSlug.includes(slug)) {
          return p;
        }
      }
    }
    return price || 0;
  };

  // Calculate estimated monthly billing using actual pricing
  const calculateMonthlyBilling = () => {
    let total = 0;
    const droplets = services.droplets || [];
    const backupPct = pricingSettings?.backupBasePctOfDroplet || 0.2;
    const snapshotPerGb = pricingSettings?.snapshotPerGbUsd || 0.06;

    droplets.forEach((d) => {
      const dropletPrice = getDropletPrice(d.size);
      total += dropletPrice;

      // Add backup cost if enabled
      if (services.backups?.some((b) => b.droplet_id === d.id)) {
        const backupMargin = pricingSettings?.backupMarginPct || 0.2;
        const backupBase = dropletPrice * backupPct;
        total += backupBase * (1 + backupMargin);
      }
    });

    // Add snapshot costs
    const snapshotSize = (services.snapshots || []).reduce(
      (sum, s) => sum + (parseFloat(s.size_gb) || 0),
      0,
    );
    const snapshotMargin = pricingSettings?.snapshotMarginPct || 0.2;
    total += snapshotSize * snapshotPerGb * (1 + snapshotMargin);

    return total;
  };

  const monthlyBilling = calculateMonthlyBilling();

  // Get order history (droplets by creation date) with actual pricing
  const orderHistory = (services.droplets || [])
    .map((d) => {
      const price = getDropletPrice(d.size);
      return {
        id: d.id,
        type: "Droplet",
        name: d.name,
        size: d.size,
        region: d.region,
        image: d.image || d.image_slug,
        status: d.status,
        created_at: d.created_at,
        price: price > 0 ? price : null, // null if price not found
      };
    })
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "services", label: "Services" },
    { id: "usage", label: "Usage & Stats" },
    { id: "billing", label: "Billing" },
    { id: "orders", label: "Order History" },
  ];

  const overviewColumns = [
    { key: "label", label: "Metric" },
    { key: "value", label: "Value" },
  ];

  const billingColumns = [
    { key: "item", label: "Item" },
    { key: "quantity", label: "Quantity" },
    { key: "unit_price", label: "Unit Price (USD)" },
    { key: "monthly_total", label: "Monthly Total (USD)" },
  ];

  const orderColumns = [
    { key: "name", label: "Product" },
    { key: "type", label: "Type" },
    { key: "size", label: "Size/Plan" },
    { key: "region", label: "Region" },
    {
      key: "price",
      label: "Price/Month",
      render: (value) => (value ? `₹${value.toFixed(2)}` : "N/A"),
    },
    {
      key: "created_at",
      label: "Ordered On",
      render: (value) => (value ? new Date(value).toLocaleString() : "-"),
    },
    {
      key: "status",
      label: "Status",
      render: (value) => (
        <span
          className={
            value === "active"
              ? "admin-badge admin-badge-active"
              : "admin-badge admin-badge-inactive"
          }
        >
          {value || "Unknown"}
        </span>
      ),
    },
  ];

  const overviewData = [
    { id: 1, label: "Total Droplets", value: stats.totalDroplets },
    { id: 2, label: "Active Droplets", value: stats.activeDroplets },
    { id: 3, label: "Total Snapshots", value: stats.totalSnapshots },
    { id: 4, label: "Total Backups", value: stats.totalBackups },
    { id: 5, label: "Total Firewalls", value: stats.totalFirewalls },
    {
      id: 6,
      label: "Total Disk Space (GB)",
      value: `${stats.totalDiskSize} GB`,
    },
  ];

  // Calculate actual billing breakdown with detailed droplet prices
  const dropletBilling = (services.droplets || []).map((d) => {
    const price = getDropletPrice(d.size);
    // Debug: log if price is 0 to identify missing prices
    if (price === 0 && d.size) {
      console.warn(
        `Price not found for droplet ${d.name} with size: ${d.size}`,
      );
    }
    return {
      id: d.id,
      name: d.name,
      size: d.size,
      price,
    };
  });

  const dropletTotal = dropletBilling.reduce((sum, d) => {
    const price = d.price || 0;
    return sum + price;
  }, 0);

  const backupBilling = (services.droplets || [])
    .map((d) => {
      if (services.backups?.some((b) => b.droplet_id === d.id)) {
        const dropletPrice = getDropletPrice(d.size);
        const backupPct = pricingSettings?.backupBasePctOfDroplet || 0.2;
        const backupMargin = pricingSettings?.backupMarginPct || 0.2;
        const backupBase = dropletPrice * backupPct;
        return {
          id: d.id,
          name: d.name,
          price: backupBase * (1 + backupMargin),
        };
      }
      return null;
    })
    .filter(Boolean);

  const backupTotal = backupBilling.reduce((sum, b) => sum + b.price, 0);

  const snapshotSize = (services.snapshots || []).reduce(
    (sum, s) => sum + (parseFloat(s.size_gb) || 0),
    0,
  );
  const snapshotPerGb = pricingSettings?.snapshotPerGbUsd || 0.06;
  const snapshotMargin = pricingSettings?.snapshotMarginPct || 0.2;
  const snapshotUnitPrice = snapshotPerGb * (1 + snapshotMargin);
  const snapshotTotal = snapshotSize * snapshotUnitPrice;

  const billingData = [
    {
      id: 1,
      item: "Droplets",
      quantity: stats.totalDroplets,
      unit_price:
        dropletTotal > 0 && stats.totalDroplets > 0
          ? `₹${(dropletTotal / stats.totalDroplets).toFixed(2)} avg`
          : "N/A",
      monthly_total: `₹${dropletTotal.toFixed(2)}`,
      details: dropletBilling,
    },
    {
      id: 2,
      item: "Backups",
      quantity: stats.totalBackups,
      unit_price:
        backupTotal > 0 && stats.totalBackups > 0
          ? `₹${(backupTotal / stats.totalBackups).toFixed(2)} avg`
          : "N/A",
      monthly_total: `₹${backupTotal.toFixed(2)}`,
      details: backupBilling,
    },
    {
      id: 3,
      item: "Snapshots (per GB)",
      quantity: `${snapshotSize.toFixed(2)} GB`,
      unit_price: `₹${snapshotUnitPrice.toFixed(4)}`,
      monthly_total: `₹${snapshotTotal.toFixed(2)}`,
    },
  ];

  return (
    <>
      <div className="admin-page-header">
        <div className="section-header-actions">
          <div>
            <h1 className="admin-page-title">User Details</h1>
            <p className="admin-page-subtitle">{user.email}</p>
          </div>
          <div className="user-detail-actions">
            {user.suspended ? (
              <button
                className="btn-primary"
                onClick={handleUnsuspend}
                disabled={suspending}
              >
                {suspending ? "Processing..." : "Unsuspend User"}
              </button>
            ) : (
              <button
                className="btn-danger"
                onClick={handleSuspendClick}
                disabled={
                  suspending ||
                  (adminEmail &&
                    user.email.toLowerCase() === adminEmail.toLowerCase())
                }
                title={
                  adminEmail &&
                  user.email.toLowerCase() === adminEmail.toLowerCase()
                    ? "Cannot suspend your own account"
                    : "Suspend user"
                }
              >
                {suspending ? "Processing..." : "Suspend User"}
              </button>
            )}
            <button
              className="btn-secondary btn-sm"
              onClick={() => navigate("/admin/users")}
            >
              ← Back to Users
            </button>
          </div>
        </div>
      </div>

      <div className="admin-section">
        <div className="admin-section-header">
          <h3 className="admin-section-title">User Information</h3>
        </div>
        <div className="user-info-grid">
          <div>
            <div className="user-info-label">User ID</div>
            <div className="user-info-value">{user.id}</div>
          </div>
          <div>
            <div className="user-info-label">Email</div>
            <div className="user-info-value">{user.email}</div>
          </div>
          <div>
            <div className="user-info-label">WHMCS ID</div>
            <div className="user-info-value">{user.whmcs_user_id || "-"}</div>
          </div>
          <div>
            <div className="user-info-label">Status</div>
            <div>
              {user.suspended ? (
                <span className="admin-badge admin-badge-suspended">
                  Suspended
                </span>
              ) : (
                <span
                  className={
                    user.is_active
                      ? "admin-badge admin-badge-active"
                      : "admin-badge admin-badge-inactive"
                  }
                >
                  {user.is_active ? "Active" : "Inactive"}
                </span>
              )}
            </div>
          </div>
          <div>
            <div className="user-info-label">Created</div>
            <div className="user-info-value">
              {user.created_at
                ? new Date(user.created_at).toLocaleString()
                : "-"}
            </div>
          </div>
        </div>
      </div>

      <div className="admin-tabs admin-tabs-with-margin">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`admin-tab ${activeTab === tab.id ? "active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "overview" && (
        <div className="admin-tab-content active">
          <div className="admin-section">
            <div className="admin-section-header">
              <h3 className="admin-section-title">Quick Overview</h3>
            </div>
            <AdminTable
              columns={overviewColumns}
              data={overviewData}
              loading={false}
              emptyMessage="No data available"
            />
          </div>
        </div>
      )}

      {activeTab === "services" && (
        <div className="admin-tab-content active">
          <UserServicesView userId={user.id} />
        </div>
      )}

      {activeTab === "usage" && (
        <div className="admin-tab-content active">
          <div className="admin-section">
            <div className="admin-section-header">
              <h3 className="admin-section-title">Usage Statistics</h3>
            </div>
            <div className="usage-stats-grid">
              <div className="card stat-card">
                <div className="stat-card-label">Total Resources</div>
                <div className="stat-card-value">
                  {stats.totalDroplets +
                    stats.totalSnapshots +
                    stats.totalBackups +
                    stats.totalFirewalls}
                </div>
              </div>
              <div className="card stat-card">
                <div className="stat-card-label">Disk Usage</div>
                <div className="stat-card-value">{stats.totalDiskSize} GB</div>
              </div>
              <div className="card stat-card">
                <div className="stat-card-label">Active Services</div>
                <div className="stat-card-value">{stats.activeDroplets}</div>
              </div>
              <div className="card stat-card">
                <div className="stat-card-label">Estimated Monthly Cost</div>
                <div className="stat-card-value">
                  ${monthlyBilling.toFixed(2)}
                </div>
              </div>
            </div>
            <AdminTable
              columns={overviewColumns}
              data={overviewData}
              loading={false}
              emptyMessage="No usage data"
            />
          </div>
        </div>
      )}

      {activeTab === "billing" && (
        <div className="admin-tab-content active">
          <div className="admin-section">
            <div className="admin-section-header admin-section-header-with-action">
              <h3 className="admin-section-title">Billing Summary</h3>
              <div className="admin-section-header-text">
                Estimated Monthly Cost:{" "}
                <strong className="admin-section-header-price">
                  ${monthlyBilling.toFixed(2)}
                </strong>
              </div>
            </div>

            {/* Detailed Droplet Breakdown */}
            {dropletBilling.length > 0 && (
              <div className="billing-breakdown-section">
                <h4 className="billing-breakdown-title">Droplet Breakdown</h4>
                <AdminTable
                  columns={[
                    { key: "name", label: "Droplet Name" },
                    { key: "size", label: "Size" },
                    {
                      key: "price",
                      label: "Monthly Price (USD)",
                      render: (value) =>
                        value > 0 ? `₹${value.toFixed(2)}` : "N/A",
                    },
                  ]}
                  data={dropletBilling}
                  loading={false}
                  emptyMessage="No droplets"
                />
                <div className="billing-total-container">
                  <strong>Droplet Total:</strong> ${dropletTotal.toFixed(2)}
                </div>
              </div>
            )}

            {/* Backup Breakdown */}
            {backupBilling.length > 0 && (
              <div className="billing-breakdown-section">
                <h4 className="billing-breakdown-title">Backup Breakdown</h4>
                <AdminTable
                  columns={[
                    { key: "name", label: "Droplet Name" },
                    {
                      key: "price",
                      label: "Monthly Price (USD)",
                      render: (value) =>
                        value > 0 ? `₹${value.toFixed(2)}` : "N/A",
                    },
                  ]}
                  data={backupBilling}
                  loading={false}
                  emptyMessage="No backups"
                />
                <div className="billing-total-container">
                  <strong>Backup Total:</strong> ${backupTotal.toFixed(2)}
                </div>
              </div>
            )}

            {/* Summary Table */}
            <AdminTable
              columns={billingColumns}
              data={billingData.map(({ details, ...rest }) => rest)}
              loading={false}
              emptyMessage="No billing information"
            />

            <div className="billing-summary-note">
              <p className="billing-summary-note-text">
                <strong>Note:</strong> Billing estimates are based on current
                pricing configuration with margins applied. Actual billing may
                vary based on usage patterns and DigitalOcean's pricing.
              </p>
            </div>
          </div>
        </div>
      )}

      {activeTab === "orders" && (
        <div className="admin-tab-content active">
          <div className="admin-section">
            <div className="admin-section-header admin-section-header-with-action">
              <h3 className="admin-section-title">Order History</h3>
              <div className="admin-section-header-text">
                Total Orders: <strong>{orderHistory.length}</strong>
              </div>
            </div>
            <AdminTable
              columns={orderColumns}
              data={orderHistory}
              loading={false}
              emptyMessage="No order history found"
            />
          </div>
        </div>
      )}

      <ConfirmModal
        show={confirmSuspend}
        title="Suspend User"
        message={`Are you sure you want to suspend "${user?.email}"? They will not be able to login until unsuspended.`}
        confirmText="Suspend"
        cancelText="Cancel"
        variant="danger"
        onConfirm={handleSuspend}
        onClose={() => setConfirmSuspend(false)}
      />
    </>
  );
}
